import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, CircularProgress, Chip } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface UpcomingEvent {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  imageUrl: string;
}

export default function UpcomingEvents() {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'news_section_upcoming_events'));
      const data: UpcomingEvent[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as UpcomingEvent);
      });
      data.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
      setEvents(data);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Upcoming Events — Kottayil Temple | വരാനിരിക്കുന്ന പരിപാടികൾ" description="Upcoming events, festivals, and celebrations at Kottayil Temple (Kotta-Kovilakam Kshethram). Plan your visit to Muchukunnu." path="/news/upcoming" keywords="Kottayil temple upcoming events, temple festivals, upcoming festivals, temple calendar, കൊട്ടയിൽ ഉത്സവം, വരാനിരിക്കുന്ന പരിപാടികൾ" />
      <PageBanner title="Upcoming Events" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #064e3b 0%, #22c55e 40%, #86efac 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <CalendarMonthIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Upcoming Events & Festivals
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      Mark your calendars for these divine celebrations
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  Plan your visits and be part of our vibrant festivals, special poojas, and community programs.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<CalendarMonthIcon sx={{ fontSize: '16px !important', color: '#22c55e !important' }} />} label="Festival Calendar" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#064e3b', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<EventAvailableIcon sx={{ fontSize: '16px !important', color: '#86efac !important' }} />} label="Upcoming Programs" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: GOLD }} />
              </Box>
            ) : events.length === 0 ? (
              <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 4, border: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#aaa' }}>No upcoming events at this time. Check back soon!</Typography>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                {events.map((event, idx) => {
                  const cardGradients = [['#22c55e','#16a34a'], ['#3b82f6','#2563eb'], ['#f59e0b','#d97706'], ['#8b5cf6','#7c3aed'], ['#ef4444','#dc2626'], ['#06b6d4','#0891b2']];
                  const g = cardGradients[idx % cardGradients.length];
                  return (
                    <Grid item xs={12} sm={6} key={event.id}>
                      <Box sx={{
                        bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                        transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        height: '100%', display: 'flex', flexDirection: 'column',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 48px rgba(0,0,0,0.1)' },
                      }}>
                        {event.imageUrl && (
                          <Box sx={{ position: 'relative' }}>
                            <Box component="img" src={event.imageUrl} alt={event.title}
                              sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                            />
                            {/* Date badge */}
                            <Box sx={{
                              position: 'absolute', top: 12, right: 12,
                              background: `linear-gradient(135deg, ${g[0]}, ${g[1]})`,
                              borderRadius: '14px', px: 1.5, py: 0.8, textAlign: 'center',
                              boxShadow: `0 6px 20px ${g[0]}40`,
                            }}>
                              <Typography sx={{ fontSize: { xs: '0.92rem', md: '1.1rem' }, fontWeight: 900, color: '#fff', lineHeight: 1 }}>
                                {new Date(event.startDate).getDate()}
                              </Typography>
                              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase' }}>
                                {new Date(event.startDate).toLocaleDateString('en-IN', { month: 'short' })}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {/* Gradient header */}
                        <Box sx={{ background: `linear-gradient(135deg, ${g[0]}, ${g[1]})`, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.2)' }}>
                            <CalendarMonthIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff', flex: 1, lineHeight: 1.3 }}>
                            {event.title}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 1, py: 0.3, borderRadius: '8px', background: `linear-gradient(135deg, ${g[0]}10, ${g[1]}10)`, border: `1px solid ${g[0]}20` }}>
                              <CalendarMonthIcon sx={{ fontSize: 12, color: g[0] }} />
                              <Typography sx={{ fontSize: '0.62rem', color: g[0], fontWeight: 700 }}>
                                {new Date(event.startDate).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                                {event.endDate && event.endDate !== event.startDate && ` - ${new Date(event.endDate).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}`}
                              </Typography>
                            </Box>
                            {event.location && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 1, py: 0.3, borderRadius: '8px', background: `linear-gradient(135deg, ${GOLD}10, ${GOLD}10)`, border: `1px solid ${GOLD}20` }}>
                                <PlaceIcon sx={{ fontSize: 12, color: GOLD }} />
                                <Typography sx={{ fontSize: '0.62rem', color: GOLD, fontWeight: 700 }}>{event.location}</Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: '0.78rem', color: '#777', lineHeight: 1.9, flex: 1 }}>
                            {event.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* DARK SLATE FOOTER */}
            <Box sx={{
              mt: 3, background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 },
              position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>Join Our Celebrations</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
                  Be part of our vibrant community events and sacred festivals.
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="news" activePath="/news/upcoming" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
